/* eslint-disable no-unused-expressions */
import React, { useMemo, useCallback, useState, useEffect } from "react";
import {
  Snackbar,
  SnackbarContent,
  Table,
  TableBody,
  TableRow,
  IconButton,
  TextField,
  Box,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import { Add, Remove, AddCircleOutline } from "@mui/icons-material";
import { styled } from "@mui/system";
import { TableLoading } from "../../sdk";
import {
  CardContainer,
  Head,
  HeaderCard,
  Tabhead,
  TCell,
  TContainer,
  THead,
  TRow,
} from "../Styles";
import { TextFieldMedium } from "../../PrimarySetups/components/Styles";
import ConfirmationDialog from "../../PrimarySetups/components/ConfirmationDialog";

// Constants
const actionButtonClass = "action-button";

const TABLE_STYLES = {
  container: { maxHeight: "50vh" },
  table: {
    width: "100%",
    border: "1px solid rgba(0, 0, 0, 0.12)",
    borderRadius: "5px",
  },
  emptyCell: { textAlign: "center", padding: "20px" },
};

// Utility Functions
const formatTime = (timeString) => {
  if (!timeString) return "";
  return timeString.split(":").slice(0, 2).join(":");
};

const getTimeKeys = (data) => Object.keys(data || {});

const DeleteRowDialog = ({
  confirmDialogOpen,
  setConfirmDialogOpen,
  handlerFunction,
  isMultiplier,
}) => {
  return (
    <Dialog
      open={confirmDialogOpen}
      onClose={() => setConfirmDialogOpen(false)}
    >
      <DialogTitle>Confirm Deletion</DialogTitle>
      <DialogContent>
        Are you sure you want to delete this{" "}
        {isMultiplier ? "multiplier" : "row"}?
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setConfirmDialogOpen(false)} color="primary">
          No
        </Button>
        <Button onClick={handlerFunction} color="error">
          Yes
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const TimeSlotCell = React.memo(
  ({
    timeKey,
    setTableData,
    editMode,
    timeSlots,
    onAddTimeSlotHandler,
    onRemoveTimeSlotHandler,
    onTimeKeyChange,
  }) => {
    return (
      <TimeCell rowSpan={timeSlots.length}>
        <TimeDisplay>
          {editMode ? (
            <TextField
              select
              value={timeKey?.slice(0, 2) || ""}
              onChange={(e) => {
                const hour = e.target.value.padStart(2, "0");
                const normalizedTime = `${hour}:00:00`;
                onTimeKeyChange?.(setTableData, timeKey, normalizedTime);
              }}
              size="small"
              sx={{ width: 100 }}
              SelectProps={{
                MenuProps: {
                  PaperProps: {
                    sx: {
                      maxHeight: 200,
                    },
                  },
                },
              }}
            >
              {[...Array(24)].map((_, i) => {
                const hour = i.toString().padStart(2, "0");
                return (
                  <MenuItem key={hour} value={hour}>
                    {hour}:00
                  </MenuItem>
                );
              })}
            </TextField>
          ) : (
            <Box>{formatTime(timeKey)}</Box>
          )}
        </TimeDisplay>

        {editMode && (
          <ActionCell>
            <IconButton
              className={actionButtonClass}
              onClick={() => onAddTimeSlotHandler?.(setTableData)}
              size="small"
              color="primary"
            >
              <Add />
            </IconButton>
            <IconButton
              className={actionButtonClass}
              onClick={() => onRemoveTimeSlotHandler?.(timeKey)}
              size="small"
              color="error"
            >
              <Remove />
            </IconButton>
          </ActionCell>
        )}
      </TimeCell>
    );
  }
);

const EditableCell = React.memo(
  ({
    editMode,
    value,
    onChange,
    type = "number",
    inputProps = {},
    suffix = "",
  }) =>
    editMode ? (
      <TextFieldMedium
        type={type}
        value={value ?? ""}
        onChange={onChange}
        inputProps={inputProps}
        size="small"
      />
    ) : (
      `${value || (type === "number" ? 0 : "--")}${suffix}`
    )
);

const ActionButtons = React.memo(
  ({
    timeKey,
    slotId,
    setTableData,
    slotIndex,
    timeSlots,
    onAddRowHandler,
    onRemoveRowHandler,
  }) => (
    <ActionButtonGroup>
      <IconButton
        className={actionButtonClass}
        onClick={() => onAddRowHandler?.(setTableData, timeKey, slotIndex)}
        size="small"
        color="primary"
      >
        <Add />
      </IconButton>
      {timeSlots.length > 1 && (
        <IconButton
          className={actionButtonClass}
          onClick={() =>
            onRemoveRowHandler?.(setTableData, timeKey, slotIndex, slotId)
          }
          size="small"
          color="error"
        >
          <Remove />
        </IconButton>
      )}
    </ActionButtonGroup>
  )
);

const EmptyState = React.memo(({ handleInitialAddTimeSlot }) => (
  <TableRow>
    <TCell colSpan={5} style={TABLE_STYLES.emptyCell}>
      No data available. Click + to add a new time slot.
      <IconButton onClick={handleInitialAddTimeSlot}>
        <AddCircleOutline />
      </IconButton>
    </TCell>
  </TableRow>
));

const TableContent = ({
  timeKeys,
  lastDayData,
  setTableData,
  editMode,
  onChange,
  onAddTimeSlotHandler,
  onRemoveTimeSlotHandler,
  onAddRowHandler,
  onRemoveRowHandler,
  handleInitialAddTimeSlot,
  onTimeKeyChangeHandler,
}) => {
  if (timeKeys.length === 0) {
    return <EmptyState handleInitialAddTimeSlot={handleInitialAddTimeSlot} />;
  }

  return timeKeys.map((timeKey) => {
    const timeSlots = lastDayData[timeKey] || [];
    return timeSlots.map((slot, slotIndex) => {
      const isFirstSlotOfTime = slotIndex === 0;
      return (
        <TableRow key={`${timeKey}-${slotIndex}`}>
          {isFirstSlotOfTime && (
            <TimeSlotCell
              timeKey={timeKey}
              setTableData={setTableData}
              editMode={editMode}
              onTimeKeyChange={onTimeKeyChangeHandler}
              timeSlots={timeSlots}
              onAddTimeSlotHandler={onAddTimeSlotHandler}
              onRemoveTimeSlotHandler={onRemoveTimeSlotHandler}
            />
          )}
          <TCell>
            <EditableCell
              editMode={editMode}
              value={slot?.minimumRooms}
              onChange={(e) =>
                onChange?.(
                  setTableData,
                  timeKey,
                  slotIndex,
                  "minimumRooms",
                  parseInt(e.target.value)
                )
              }
              inputProps={{ min: 0 }}
            />
          </TCell>
          <TCell>
            <EditableCell
              editMode={editMode}
              value={slot?.maximumRooms}
              onChange={(e) =>
                onChange?.(
                  setTableData,
                  timeKey,
                  slotIndex,
                  "maximumRooms",
                  parseInt(e.target.value)
                )
              }
              inputProps={{ min: 0 }}
            />
          </TCell>
          <TCell>
            <EditableCell
              editMode={editMode}
              value={slot?.multiplier}
              onChange={(e) =>
                onChange?.(
                  setTableData,
                  timeKey,
                  slotIndex,
                  "multiplier",
                  parseFloat(e.target.value)
                )
              }
              inputProps={{ min: 0, step: 0.01 }}
            />
          </TCell>
          {editMode && (
            <TCell>
              <ActionButtons
                timeKey={timeKey}
                slotId={slot?.id}
                setTableData={setTableData}
                slotIndex={slotIndex}
                timeSlots={timeSlots}
                onAddRowHandler={onAddRowHandler}
                onRemoveRowHandler={onRemoveRowHandler}
              />
            </TCell>
          )}
        </TableRow>
      );
    });
  });
};

// Main Component
export const LastDayMultiplier = React.memo(
  ({
    lastDayData = {},
    setTableData,
    applicableDay,
    isWeekDay,
    onChange,
    open = null,
    handleCloseStatus,
    networkMsg = "",
    onRemoveRowHandler,
    onAddRowHandler,
    onAddTimeSlotHandler,
    onRemoveTimeSlotHandler,
    onTimeKeyChange,
    editMode,
    setEditMode,
    apiResponseData,
    loading = false,
    deleteTimeSlotRow,
    deleteRowInTimeSlot,
  }) => {
    const [pickedTimeSlots, setPickedTimeSlots] = useState([]);
    const [deleteTimeSlotDialog, setDeleteTimeSlotDialog] = useState(false);
    const [deleteTargetIdOrKey, setDeleteTargetIdOrKey] = useState("");
    const [timeSlotWarningDialog, setTimeSlotWarningDialog] = useState({
      open: false,
    });
    const [deleteRowInTimeSlotDialog, setDeleteRowInTimeSlotDialog] = useState(
      false
    );
    const [deleteRowId, setDeleteRowId] = useState({
      timeKey: null,
      slotIndex: null,
      rowId: null,
    });

    useEffect(() => {
      setPickedTimeSlots(Object.keys(lastDayData));
    }, [lastDayData]);

    const handleInitialAddTimeSlot = useCallback(() => {
      setEditMode((prev) => ({ ...prev, [applicableDay]: true }));
      onAddTimeSlotHandler?.(
        setTableData,
        applicableDay,
        isWeekDay,
        "12:00:00"
      );
    }, [
      setEditMode,
      onAddTimeSlotHandler,
      setTableData,
      applicableDay,
      isWeekDay,
    ]);

    const timeKeys = useMemo(() => getTimeKeys(lastDayData), [lastDayData]);

    const handleRemoveRow = (setTableData, timeKey, slotIndex, rowId) => {
      if (rowId) {
        setDeleteRowId({ rowId, timeKey: null, slotIndex: null });
      } else {
        setDeleteRowId({ rowId: null, timeKey, slotIndex });
      }
      setDeleteRowInTimeSlotDialog(true);
    };

    const handleRemoveTimeSlotRow = (timeKey) => {
      setDeleteTargetIdOrKey(timeKey);
      setDeleteTimeSlotDialog(true);
    };

    const onDeleteTimeRowHandler = async () => {
      if (deleteRowId.rowId) {
        await deleteRowInTimeSlot(deleteRowId.rowId, applicableDay);
      } else {
        onRemoveRowHandler?.(
          setTableData,
          deleteRowId.timeKey,
          deleteRowId.slotIndex
        );
      }
      setDeleteRowId({ timeKey: null, slotIndex: null, rowId: null });
      setDeleteRowInTimeSlotDialog(false);
    };

    const onDeleteTimeSlotHandler = async () => {
      const day = isWeekDay ? "weekday" : "weekend";

      const isFindKey = Object.keys(
        apiResponseData?.[applicableDay]?.[day] || {}
      )?.includes(deleteTargetIdOrKey);
      if (isFindKey && deleteTargetIdOrKey) {
        await deleteTimeSlotRow(deleteTargetIdOrKey, applicableDay, isWeekDay);
      } else {
        onRemoveTimeSlotHandler?.(setTableData, deleteTargetIdOrKey);
      }
      setDeleteTimeSlotDialog(false);
    };

    const onTimeKeyChangeHandler = (setTableData, oldTimeKey, newTimeValue) => {
      if (oldTimeKey === newTimeValue) {
        return;
      }
      const isTimeSlotAlreadyExists = pickedTimeSlots.some(
        (item) => item === newTimeValue
      );
      if (isTimeSlotAlreadyExists) {
        setTimeSlotWarningDialog({
          open: true,
          setTableData,
          oldTimeKey,
          newTimeValue,
        });
      } else {
        onTimeKeyChange?.(setTableData, oldTimeKey, newTimeValue);
      }
    };

    const handleAddRowTimeKey = async (setTableData) => {
      await onAddTimeSlotHandler?.(setTableData, applicableDay, isWeekDay);
    };

    const handleAddRow = async (setTableData, timeKey, slotIndex) => {
      await onAddRowHandler?.(
        setTableData,
        applicableDay,
        isWeekDay,
        timeKey,
        slotIndex
      );
    };

    if (loading === applicableDay || loading === "all") {
      return (
        <CardContainer>
          <HeaderCard>
            <Head>
              <div className="title">Last Day Multiplier</div>
            </Head>
          </HeaderCard>
          <TableLoading />
        </CardContainer>
      );
    }

    return (
      <>
        <TContainer style={TABLE_STYLES.container}>
          <Table
            stickyHeader
            aria-label="last day multiplier table"
            style={TABLE_STYLES.table}
          >
            <THead>
              <TRow>
                <Tabhead>Time</Tabhead>
                <Tabhead>Minimum Available Rooms</Tabhead>
                <Tabhead>Maximum Available Rooms</Tabhead>
                <Tabhead>Value</Tabhead>
                {editMode && <Tabhead>Actions</Tabhead>}
              </TRow>
            </THead>
            <TableBody>
              <TableContent
                timeKeys={timeKeys}
                lastDayData={lastDayData}
                setTableData={setTableData}
                onChange={onChange}
                editMode={editMode}
                onAddTimeSlotHandler={handleAddRowTimeKey}
                onRemoveTimeSlotHandler={handleRemoveTimeSlotRow}
                onAddRowHandler={handleAddRow}
                onRemoveRowHandler={handleRemoveRow}
                handleInitialAddTimeSlot={handleInitialAddTimeSlot}
                onTimeKeyChangeHandler={onTimeKeyChangeHandler}
              />
            </TableBody>
          </Table>
        </TContainer>

        <DeleteRowDialog
          confirmDialogOpen={deleteTimeSlotDialog}
          setConfirmDialogOpen={setDeleteTimeSlotDialog}
          handlerFunction={onDeleteTimeSlotHandler}
          isMultiplier={true}
        />

        <DeleteRowDialog
          confirmDialogOpen={deleteRowInTimeSlotDialog}
          setConfirmDialogOpen={setDeleteRowInTimeSlotDialog}
          handlerFunction={onDeleteTimeRowHandler}
          isMultiplier={false}
        />

        <ConfirmationDialog
          warningIcon
          handleCloseConfirmation={() =>
            setTimeSlotWarningDialog({ open: false })
          }
          confirmationText={
            "Duplicate Multiplier Time Found. Do you want to remove the previous multiplier?"
          }
          onSuccess={() => {
            const {
              setTableData,
              oldTimeKey,
              newTimeValue,
            } = timeSlotWarningDialog;
            onTimeKeyChange?.(setTableData, oldTimeKey, newTimeValue);
            setTimeSlotWarningDialog({ open: false });
          }}
          open={timeSlotWarningDialog.open}
        />

        {open ? (
          <Snackbar
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            open={open}
            autoHideDuration={3000}
            onClose={handleCloseStatus}
          >
            <SnackbarContent
              style={{
                backgroundColor: open === "error" ? "#CA3433" : "#228b22",
              }}
              message={networkMsg}
            />
          </Snackbar>
        ) : null}
      </>
    );
  }
);

// LastDayMultiplier.displayName = "LastDayMultiplier";

// Styled Components
const TimeCell = styled(TCell)`
  border-right: 1px solid rgba(224, 224, 224, 1);
  vertical-align: middle;
  position: relative;
`;

const ActionCell = styled("div")`
  display: flex;
  flex-direction: row;
  gap: 4px;
  align-items: center;
  border-top: 1px solid #ddd;
  justify-content: center;
  padding: 8px 4px;
  .action-button {
    padding: 0;
    margin: 0 5px;
  }
`;

const TimeDisplay = styled("div")`
  text-align: center;
  font-weight: bold;
  padding: 8px 0;
  margin-top: 8px;
`;

const ActionButtonGroup = styled("div")`
  display: flex;
  justify-content: center;
  .action-button {
    padding: 0;
    margin: 0 4px;
  }
`;
